import { BookOpen, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { ApiError, listLibrary } from "@/api/client";
import type { LibraryEntry } from "@/api/client";
import { NothingLearned } from "@/screens/knowledge/NothingLearned";
import { EmptyState } from "@/ui/EmptyState";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

/** How much of the past evidence agrees with the entry's name. */
function shareOf(entry: LibraryEntry): string {
  const n = entry.mark ? (entry.codes[entry.mark] ?? 0) : 0;
  return entry.evidence ? `${Math.round((n / entry.evidence) * 100)}%` : "—";
}

function matches(entry: LibraryEntry, needle: string): boolean {
  const hay = [entry.mark, entry.config_key, entry.notes, entry.source_filename]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/**
 * What estimators have taught about configurations (ADR-0011): a type's name and notes,
 * proposed again on the next set with the same make-up. Read-only until the
 * `library:manage` permission lets owners and admins edit it.
 */
export function TypesTab() {
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    listLibrary()
      .then((got) => {
        if (!cancelled) setEntries(got.entries);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEntries([]);
        setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (entries ?? []).filter((e) => !needle || matches(e, needle));
  }, [entries, query]);

  return (
    <section aria-label="Types">
      <p className="max-w-2xl text-sm text-ink-2">
        Type names and notes your estimators have taught. A new set whose panels have the same
        make-up starts with them, still proposed.
      </p>

      {entries && entries.length > 0 ? (
        <label className="mt-4 block max-w-md">
          <span className="sr-only">Search the library</span>
          <Input
            type="search"
            icon={<Search />}
            placeholder="Search names, notes and sets"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      ) : null}

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      <div className="mt-4">
        {entries === null ? (
          <div className="space-y-2" role="status" aria-label="Loading the library">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <>
            <EmptyState icon={<BookOpen />} title="Nothing taught yet">
              Name a type or add notes to it in a take-off, and the library keeps it for the next
              set.
            </EmptyState>
            <NothingLearned />
          </>
        ) : shown.length === 0 ? (
          <EmptyState icon={<Search />} title="Nothing matches">
            Try another word.
          </EmptyState>
        ) : (
          <Table aria-label="Library">
            <thead>
              <tr>
                <Th>Name</Th>
                <Th className="hidden md:table-cell">Configuration</Th>
                <Th>Notes</Th>
                <Th className="hidden lg:table-cell">Taught by</Th>
                <Th className="text-right">Used</Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((entry) => (
                <tr key={entry.id}>
                  <Td className="font-medium whitespace-nowrap text-ink">
                    {entry.mark ?? <span className="text-muted">Unnamed</span>}
                    {entry.evidence > 0 ? (
                      <span className="block text-xs font-normal text-muted">
                        {shareOf(entry)} of {entry.evidence.toLocaleString()} past panels
                      </span>
                    ) : null}
                  </Td>
                  <Td className="hidden font-mono text-xs text-ink-2 md:table-cell">
                    {entry.config_key}
                  </Td>
                  <Td className="max-w-80 text-ink-2">
                    <span className="line-clamp-2">{entry.notes || "—"}</span>
                  </Td>
                  <Td className="hidden max-w-56 lg:table-cell">
                    <span className="block text-xs text-muted">
                      {entry.mark_source === "takeoff" ? "Past take-off" : "An estimator"}
                    </span>
                    {entry.source_document_id ? (
                      <Link
                        to={
                          entry.mark_source === "takeoff"
                            ? `/knowledge/sources/${entry.source_document_id}`
                            : `/documents/${entry.source_document_id}`
                        }
                        className="block truncate text-ink-2 hover:text-accent"
                        title={entry.source_filename ?? undefined}
                      >
                        {entry.source_filename ?? "A set"}
                      </Link>
                    ) : (
                      <span className="block truncate text-muted">
                        {entry.source_filename ?? "—"}
                      </span>
                    )}
                  </Td>
                  <Td className="tabular text-right text-ink-2">{entry.uses}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </section>
  );
}
